import React, { useEffect } from 'react';
import './index.scss';
import _ from 'lodash';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '@/components/Icon';
import { settingBillingThunk } from '@/store/reducers/settingBillingReducer';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  BILLING_TAB_PARAM,
  BILLING_TABS,
  getBillingPath,
  getBillingTab
} from '../../config/billing';

type SettingsNavigationItem = {
  key: string;
  icon?: string;
  iconNode?: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

export default function SettingNavigation(): React.ReactElement {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = getBillingTab(new URLSearchParams(location.search).get(BILLING_TAB_PARAM));
  const isUnlimitedCreditsPlan = useAppSelector((state) => state.settingBilling.creditStatus?.plan.credit_total === '-1');

  useEffect(() => {
    dispatch(settingBillingThunk.getCreditStatus());
  }, [dispatch]);

  const creditsIcon = (
    <span className="icon credits-icon" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#settings-credits-icon)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.5862 1.41481C7.36726 0.633914 8.63333 0.633815 9.41432 1.41481L14.5862 6.58669C15.3667 7.36772 15.3669 8.6339 14.5862 9.41481L9.41432 14.5867C8.63341 15.3674 7.36723 15.3672 6.5862 14.5867L1.41432 9.41481C0.633326 8.63381 0.633427 7.36775 1.41432 6.58669L6.5862 1.41481ZM8.00026 6.00075C6.89569 6.00075 6.00026 6.89618 6.00026 8.00075C6.00042 9.10518 6.89579 10.0008 8.00026 10.0008C9.10459 10.0006 10.0001 9.10508 10.0003 8.00075C10.0003 6.89628 9.10469 6.00091 8.00026 6.00075Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="settings-credits-icon">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </span>
  );

  const settingNavigationItems: SettingsNavigationItem[] = [
    {
      key: 'navigation.settings.billing',
      icon: 'navigation/billing',
      active: activeTab === BILLING_TABS.planBilling || (isUnlimitedCreditsPlan && activeTab === BILLING_TABS.credit),
      onClick: () => navigate(getBillingPath(BILLING_TABS.planBilling))
    }
  ];

  if (!isUnlimitedCreditsPlan) {
    settingNavigationItems.unshift({
      key: 'navigation.settings.credit',
      iconNode: creditsIcon,
      active: activeTab === BILLING_TABS.credit,
      onClick: () => navigate(getBillingPath(BILLING_TABS.credit))
    });
  }

  return (
    <div className="navigation settings-navigation">
      <div className="navigation-list">
        {
          _.map(settingNavigationItems, (item) => (
            <button
              key={item.key}
              type="button"
              className={classNames('navigation-item', {
                'active medium': item.active,
                regular: !item.active
              })}
              onClick={item.onClick}
            >
              {item.iconNode ?? <Icon className={classNames('icon', item.icon && _.last(item.icon.split('/')))} icon={item.icon ?? ''} />}
              <span>{t(item.key)}</span>
            </button>
          ))
        }
      </div>
    </div>
  );
}
